type HrefParameterMap = Record<string, string | number | boolean | undefined | null>;

type ConstructHrefErrorPayload = {
  route: string;
  parameterMap?: HrefParameterMap;
  companiesCount: number;
};

type ConstructHrefError = {
  message: string;
  payload: ConstructHrefErrorPayload;
};

type ConstructHrefResult = {
  href: string;
  error: ConstructHrefError | null;
};

/**
 * Constructs an Href string by appending or updating query parameters to a given route.
 *
 * This function can handle various types of input routes:
 * - Absolute URLs (e.g., "https://example.com/path")
 * - Protocol-relative URLs (e.g., "//example.com/path")
 * - Root-relative paths (e.g., "/path/to/page")
 * - Page-relative paths (e.g., "page.html", "../other.html")
 *
 * It preserves existing query parameters and hash fragments from the original route
 * unless they are explicitly overridden or removed by the `parameterMap`.
 *
 * @param route The base route string. Can include existing query parameters and a hash fragment.
 * @param companiesCount A number representing the total count of companies available to the user.
 *                       If this is `1`, the `companyId` query parameter will be removed from the href.
 * @param parameterMap An optional object mapping query parameter keys to their values.
 *                     - If a value is a string, number, or boolean, it will be set as a query parameter.
 *                     - If a value is `null` or `undefined`, the corresponding query parameter will be removed.
 * @returns An object containing:
 *          - `href`: The reconstructed Href string with the applied query parameters and original hash.
 *                    In case of an error during processing (e.g., for invalid route structures or
 *                    unexpected issues), this will be the original `route` string.
 *          - `error`: `null` if the href was constructed successfully, or an object with `message`
 *                     and `payload` (containing the original `route` and `parameterMap`) if an error occurred.
 */
export const constructHref = (
  route: string,
  companiesCount: number,
  parameterMap?: HrefParameterMap
): ConstructHrefResult => {
  if (!route) {
    return {
      href: "",
      error: {
        message: "Empty or invalid route.",
        payload: { route, parameterMap, companiesCount },
      },
    };
  }

  let path = route;
  let queryPart = "";
  let hashPart = "";

  const hashIndex = path.indexOf("#");
  if (hashIndex !== -1) {
    hashPart = path.substring(hashIndex + 1); // the original hash fragment without '#' (e.g., "section1")
    path = path.substring(0, hashIndex);
  }

  const queryIndex = path.indexOf("?");
  if (queryIndex !== -1) {
    queryPart = path.substring(queryIndex + 1); // the original query string without '?' (e.g., "a=1&b=2")
    path = path.substring(0, queryIndex);
  }

  // If the path is empty after stripping query/hash, just return the original route
  // This handles cases like "#section" or "?param=value" which are valid in certain contexts
  if (path.trim() === "") {
    return { href: route, error: null };
  }

  // These are schemes that we don't want to process - just return them as-is
  const NON_PROCESSABLE_SCHEMES = /^(mailto|tel|sms|data|blob|ftp|file|javascript):/i;
  if (NON_PROCESSABLE_SCHEMES.test(path)) {
    return { href: route, error: null };
  }

  // Validate the path component: Check structure using new URL()
  try {
    // Attempt to parse the path with a dummy base to validate its structure.
    // If 'path' is an absolute URL (e.g., "http://example.com"), the base is ignored.
    // If 'path' is relative, it's resolved against the base. This catches malformed paths.
    new URL(path, "http://dummybase.com");
  } catch (validationError) {
    const reason =
      validationError instanceof Error ? validationError.message : String(validationError);
    return {
      href: route, // Return original route on error
      error: {
        message: `Invalid route structure: The path component ('${path}' from route '${route}') is not suitable for URL query manipulation. Reason: ${reason}`,
        payload: { route, parameterMap, companiesCount },
      },
    };
  }

  // If all validations pass, proceed with query parameter manipulation

  try {
    const searchParams = new URLSearchParams(queryPart);

    if (parameterMap) {
      for (const [key, value] of Object.entries(parameterMap)) {
        if (value === undefined || value === null) {
          searchParams.delete(key); // Remove parameter
        } else {
          searchParams.set(key, String(value)); // Set/overwrite parameter
        }
      }
    }

    // If there's only one company, remove the companyId parameter
    if (companiesCount === 1) {
      searchParams.delete("companyId");
    }

    const newQueryString = searchParams.toString();

    if (newQueryString) {
      path += `?${newQueryString}`;
    }

    if (hashPart) {
      path += `#${hashPart}`;
    }

    return { href: path, error: null };
  } catch (e) {
    // This catch handles unexpected errors during query/hash processing (e.g. URLSearchParams or string concatenation)
    const detailedErrorMessage = e instanceof Error ? e.message : "Unknown error. See payload.";
    return {
      href: route, // Return original route on error
      error: {
        message: `Error during href query/hash processing: ${detailedErrorMessage}.`,
        payload: { route, parameterMap, companiesCount },
      },
    };
  }
};
